import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiSearch, FiChevronDown, FiChevronUp, FiDownload, FiRefreshCw } from 'react-icons/fi';

const STATUS_COLORS = {
  received:  'bg-blue-100 text-blue-700',
  verified:  'bg-green-100 text-green-700',
  rejected:  'bg-red-100 text-red-700',
};

const STATUS_OPTIONS = ['received', 'verified', 'rejected'];

export default function AdminDeclarations() {
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRefId, setExpandedRefId] = useState(null);
  const [filters, setFilters] = useState({ search: '', trip: '', status: '' });
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    loadDeclarations();
    // eslint-disable-next-line
  }, [filters]);

  const loadDeclarations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      );
      const { data } = await api.get(`/declarations?${params}`);
      setDeclarations(data);
    } catch (err) {
      toast.error('Failed to load declarations');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (refId, status) => {
    setUpdatingStatus(refId);
    try {
      await api.patch(`/declarations/${refId}/status`, { status });
      setDeclarations(prev =>
        prev.map(d => d.refId === refId ? { ...d, status } : d)
      );
      toast.success(`Status updated to "${status}"`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const exportCSV = () => {
    const headers = [
      'Ref ID', 'Participant Name', 'Age', 'Mobile',
      'Emergency Contact', 'Trip', 'Trip Date', 'ID Type',
      'Status', 'Submitted At',
    ];
    const rows = declarations.map(d => [
      d.refId, d.participantName, d.age, d.mobile,
      d.emergencyContact, d.tripName, d.tripDate, d.idType,
      d.status, new Date(d.submittedAt).toLocaleString('en-IN'),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `declarations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const toggleExpand = (refId) => {
    setExpandedRefId(prev => (prev === refId ? null : refId));
  };

  const printDeclaration = (d) => {
    const idLabel = d.idType?.replace(/_/g, ' ') || '—';
    const tripDateFmt = d.tripDate
      ? new Date(d.tripDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
      : '—';
    const submittedFmt = d.submittedAt
      ? new Date(d.submittedAt).toLocaleString('en-IN')
      : '—';

    const photoHTML = d.photographBase64
      ? `<img src="${d.photographBase64}" alt="Participant Photograph" style="width:110px;height:130px;object-fit:cover;border:1px solid #ccc;border-radius:4px;" />`
      : `<div style="width:110px;height:130px;border:1px dashed #aaa;display:flex;align-items:center;justify-content:center;font-size:11px;color:#999;border-radius:4px;">No Photo</div>`;

    const idDocHTML = d.idDocumentBase64
      ? `<img src="${d.idDocumentBase64}" alt="${idLabel}" style="width:220px;height:160px;object-fit:contain;border:1px solid #ccc;border-radius:4px;background:#fff;" />`
      : `<div style="font-size:12px;color:#999;">No document uploaded</div>`;

    const sigHTML = d.signatureBase64
      ? `<img src="${d.signatureBase64}" alt="Signature" style="max-width:260px;max-height:80px;object-fit:contain;" />`
      : `<div style="font-size:12px;color:#999;">No signature captured</div>`;

    const win = window.open('', `declaration-${d.refId}`, 'width=860,height=900');
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Self Declaration – ${d.refId}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; padding: 36px 44px; background: #fff; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #184B44; padding-bottom: 12px; margin-bottom: 20px; }
    .header-left h1 { font-size: 20px; color: #184B44; }
    .header-left p { font-size: 11px; color: #555; margin-top: 3px; }
    .ref-badge { background: #f0f7f6; border: 1.5px solid #184B44; border-radius: 6px; padding: 8px 16px; text-align: center; }
    .ref-badge .label { font-size: 9px; color: #777; text-transform: uppercase; letter-spacing: 1px; }
    .ref-badge .val { font-size: 18px; font-weight: bold; color: #184B44; letter-spacing: 2px; }
    .section-title { font-size: 13px; font-weight: bold; color: #184B44; background: #eef7f6; padding: 5px 10px; border-left: 4px solid #184B44; margin: 18px 0 10px; }
    .declaration-box { border: 1px solid #ccc; border-radius: 4px; padding: 12px 14px; background: #fafafa; font-size: 11px; line-height: 1.7; color: #333; margin-bottom: 10px; }
    .declaration-box p { margin-bottom: 8px; }
    .declaration-box p:last-child { font-weight: bold; color: #111; margin-bottom: 0; }
    .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 10px; }
    .detail-row { display: flex; border-bottom: 1px solid #eee; padding: 5px 0; }
    .detail-label { width: 46%; font-weight: bold; color: #444; font-size: 11px; }
    .detail-val { font-size: 11px; color: #111; }
    .docs-row { display: flex; gap: 28px; align-items: flex-start; margin-top: 10px; }
    .doc-block { flex: 1; }
    .doc-block .doc-title { font-size: 11px; font-weight: bold; color: #555; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
    .sig-block { border: 1px solid #aaa; border-radius: 4px; padding: 10px 14px; background: #fff; display: inline-block; min-width: 280px; min-height: 90px; }
    .status-badge { display: inline-block; padding: 2px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: capitalize; }
    .received { background: #dbeafe; color: #1d4ed8; }
    .verified  { background: #dcfce7; color: #15803d; }
    .rejected  { background: #fee2e2; color: #b91c1c; }
    .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ddd; display: flex; justify-content: space-between; font-size: 10px; color: #888; }
    .page-break { page-break-before: always; break-before: page; }
    @media print {
      body { padding: 12px 20px; }
      @page {
        size: A4 portrait;
        margin: 15mm 14mm 10mm 14mm;
      }
      /* Remove browser-generated URL/title from print header and footer */
      head title { display: none; }
      html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <h1>🏔️ Garhwal Hikes</h1>
      <p>Self Declaration Form — Adventure Activity Participant</p>
    </div>
    <div class="ref-badge">
      <div class="label">Reference ID</div>
      <div class="val">${d.refId}</div>
    </div>
  </div>

  <!-- Declaration Text -->
  <div class="section-title">Declaration Text</div>
  <div class="declaration-box">
    <p>Adventure activities provided by Garhwal Hikes has its share of risk and dangers, especially in respect to the terrain, weather, high altitude, desolate nature. The possibility of slip and falls, getting injured, falling sick, and even death cannot be ruled out.</p>
    <p>Participants of this Adventure activity/trip should have good health, stamina, and should not hide or misrepresent any fact about the health as a poor state of health can greatly increase the perils that can be incurred on these Adventure activity/trip.</p>
    <p>To run such a colossal operation, Garhwal Hikes hires Third Party professionals. Garhwal Hikes will not be responsible for any harm, loss or damage to your luggage, belongings or self when carried by porters, ponies or vehicles, during the trip, your arrival and departure or stay at the hotel/guest house.</p>
    <p>Participant should behave in an acceptable manner conducive to group travel. Garhwal Hikes reserves the right to terminate your trip (if you are found to be causing distress/annoyance to co-travellers, damage property of any supplier, smoking or consumption of alcohol). No refund for unused services is possible in such cases.</p>
    <p>The rules, regulations and guidelines have been adopted for the safe enjoyment of the activity/trip. Participant must abide by the instructions given by the Trip leader/organiser, their decision will be final and will be binding with respect.</p>
    <p>Participant safety and enjoyment is Garhwal Hikes prime concern and we therefore carry out our responsibility honestly &amp; sincerely to ensure that your trip is trouble-free. Garhwal Hikes reserves the right to modify activity/trip or part of the trip under circumstances, including but not limited to, acts of God, road blockage, political unrest, sickness, accidents, events beyond Garhwal Hikes control etc. Any extra cost arising from such circumstances shall be borne by participant on the spot.</p>
    <p>Garhwal Hikes is in no way responsible for any cost related to participant medical care, evacuation during or after the Adventure activity/trip.</p>
    <p>I hereby declare that I have read all the disclaimer terms and my participation in this Adventure activity/trip is completely voluntary, and I am fully aware of the risk involved. I will not hold Garhwal Hikes wholly or partly responsible in case of any accident, illness, injury or death on this activity/trip.</p>
  </div>

  <!-- Participant Details -->
  <div class="section-title">Participant Details</div>
  <div class="details-grid">
    <div class="detail-row"><span class="detail-label">Participant Name</span><span class="detail-val">${d.participantName || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Age</span><span class="detail-val">${d.age || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Mobile Number</span><span class="detail-val">${d.mobile || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Emergency Contact Name</span><span class="detail-val">${d.emergencyContactName || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Emergency Contact No.</span><span class="detail-val">${d.emergencyContact || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Activity / Trip</span><span class="detail-val">${d.tripName || '—'}</span></div>
    <div class="detail-row"><span class="detail-label">Trip Date</span><span class="detail-val">${tripDateFmt}</span></div>
    <div class="detail-row"><span class="detail-label">ID Type Submitted</span><span class="detail-val" style="text-transform:capitalize">${idLabel}</span></div>
    <div class="detail-row"><span class="detail-label">Declaration Status</span><span class="detail-val"><span class="status-badge ${d.status || 'received'}">${d.status || 'received'}</span></span></div>
    <div class="detail-row"><span class="detail-label">Submitted At</span><span class="detail-val">${submittedFmt}</span></div>
  </div>

  <!-- Photo + ID Document — starts on page 2 -->
  <div class="page-break">
  <div class="section-title">Photographs &amp; Documents</div>
  <div class="docs-row">
    <div class="doc-block">
      <div class="doc-title">Recent Photograph</div>
      ${photoHTML}
    </div>
    <div class="doc-block">
      <div class="doc-title">Identity Document (${idLabel})</div>
      ${idDocHTML}
    </div>
  </div>

  <!-- Signature -->
  <div class="section-title">Participant Signature</div>
  <div class="sig-block">
    ${sigHTML}
  </div>
  <p style="font-size:10px;color:#888;margin-top:6px;">Digitally signed by ${d.participantName || '—'} on ${submittedFmt}</p>

  <!-- Footer -->
  <div class="footer">
    <span>Garhwal Hikes — Self Declaration Record</span>
    <span>Printed on ${new Date().toLocaleString('en-IN')}</span>
  </div>
  </div>

  <script>
    window.onload = () => {
      // Give images time to load before printing
      setTimeout(() => { window.print(); }, 400);
    };
  <\/script>
</body>
</html>`);
    win.document.close();
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 Self Declarations</h1>
          <p className="text-sm text-gray-500 mt-1">
            {declarations.length} declaration{declarations.length !== 1 ? 's' : ''} found
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadDeclarations}
            className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 text-gray-600"
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700"
          >
            <FiDownload size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search name, ref ID, mobile…"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <input
            type="text"
            placeholder="Filter by trip name…"
            value={filters.trip}
            onChange={e => setFilters(f => ({ ...f, trip: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <button
              onClick={() => setFilters({ search: '', trip: '', status: '' })}
              className="px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-6"></th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Ref ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Participant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Trip</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Trip Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Submitted</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">ID Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-gray-400">
                    <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : declarations.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-gray-400">
                    No declarations found
                  </td>
                </tr>
              ) : (
                declarations.map(d => (
                  <React.Fragment key={d.refId}>
                    {/* Main row */}
                    <tr
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRefId === d.refId ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleExpand(d.refId)}
                    >
                      <td className="px-4 py-3 text-gray-400">
                        {expandedRefId === d.refId
                          ? <FiChevronUp size={14} />
                          : <FiChevronDown size={14} />
                        }
                      </td>
                      <td className="px-4 py-3 font-mono font-medium text-gray-800">{d.refId}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{d.participantName}</p>
                        <p className="text-xs text-gray-500">{d.mobile}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{d.tripName}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(d.tripDate)}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(d.submittedAt)}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{d.idType?.replace(/_/g, ' ') || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <select
                          value={d.status}
                          disabled={updatingStatus === d.refId}
                          onChange={e => updateStatus(d.refId, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-50"
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {expandedRefId === d.refId && (
                      <tr className="bg-blue-50">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference ID</p>
                              <p className="font-mono font-bold text-gray-900">{d.refId}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Participant</p>
                              <p className="font-medium text-gray-900">{d.participantName}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Age</p>
                              <p className="text-gray-800">{d.age || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Mobile</p>
                              <p className="text-gray-800">{d.mobile}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Emergency Contact Name</p>
                              <p className="text-gray-800">{d.emergencyContactName || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Emergency Contact No.</p>
                              <p className="text-gray-800">{d.emergencyContact || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Activity / Trip</p>
                              <p className="font-medium text-gray-900">{d.tripName}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Trip Date</p>
                              <p className="text-gray-800">{formatDate(d.tripDate)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">ID Type</p>
                              <p className="text-gray-800 capitalize">{d.idType?.replace(/_/g, ' ') || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                                {d.status}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Submitted At</p>
                              <p className="text-gray-800">{d.submittedAt ? new Date(d.submittedAt).toLocaleString('en-IN') : '—'}</p>
                            </div>
                            {d.statusUpdatedBy && (
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Updated By</p>
                                <p className="text-gray-800">{d.statusUpdatedBy}</p>
                              </div>
                            )}
                          </div>

                          {/* Print / Download button */}
                          <div className="mt-4 pt-4 border-t border-blue-200 flex justify-end">
                            <button
                              onClick={() => printDeclaration(d)}
                              className="flex items-center gap-2 bg-gray-800 text-white text-xs px-4 py-2 rounded-lg hover:bg-gray-900 transition"
                            >
                              <FiDownload size={13} /> Print / Download PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
